import { registerSW } from 'virtual:pwa-register';
import { useEffect, useState } from 'react';

/**
 * Persistent toast shown when the service worker reports a new build is
 * available. Clicking "Recharger" calls the updateSW(true) returned by
 * vite-plugin-pwa, which skipWaiting + window.location.reload().
 */
export function UpdatePrompt() {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const [updateSW, setUpdateSW] = useState<((reload?: boolean) => Promise<void>) | null>(null);

  useEffect(() => {
    if (!import.meta.env.PROD) return;
    if (!('serviceWorker' in navigator)) return;

    const fn = registerSW({
      onNeedRefresh() {
        setNeedRefresh(true);
      },
      onOfflineReady() {
        setOfflineReady(true);
        // auto-dismiss the "ready" message after 5 s
        setTimeout(() => setOfflineReady(false), 5000);
      },
    });
    setUpdateSW(() => fn);
  }, []);

  if (needRefresh) {
    return (
      <output
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-accent-yellow text-ink border-[2.5px] border-ink rounded-card px-4 py-2 font-hand text-[15px] flex items-center gap-3 shadow-ink-lg"
        data-testid="update-toast"
      >
        <span aria-hidden>✨</span>
        <span>Nouvelle version disponible.</span>
        <button
          type="button"
          onClick={() => updateSW?.(true)}
          className="px-3 py-1 bg-ink text-paper border-2 border-ink rounded-control font-gloock text-[13px] shadow-ink-sm hover:translate-y-[-1px] transition-transform"
        >
          Recharger
        </button>
        <button
          type="button"
          onClick={() => setNeedRefresh(false)}
          aria-label="Plus tard"
          className="text-ink/60 hover:text-ink rounded-full w-6 h-6 grid place-items-center"
        >
          ✕
        </button>
      </output>
    );
  }

  if (offlineReady) {
    return (
      <output
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-ink text-paper border-2 border-ink rounded-card px-4 py-2 font-hand text-[14px] flex items-center gap-2 shadow-ink-lg"
        data-testid="offline-ready-toast"
      >
        <span aria-hidden>✅</span>
        <span>PharmaFiche est prête à fonctionner hors-ligne.</span>
      </output>
    );
  }

  return null;
}
